/**
 * Future Stocks Lab — experimental.
 *
 * The same categories as Stocks of the Future (plus the Top Picks "Future Technology"
 * radar), but every stock carries the bottom line from three features at one lookback:
 *
 *   Volatility Ranges   → zone, target, stop, expected return    (valid 1–2 weeks)
 *   Stock Scoring       → score, BUY/HOLD/SELL, breakdown, Cross-Feature Consensus
 *   Trading Signals     → BUY/HOLD/SELL, confidence, indicator breakdown  (valid ~7 days)
 *
 * The horizons disagree on purpose. A thesis stock can be a decade-long hold and a
 * two-week SELL at once; this page exists to show that, not to average it away. The
 * verdict chip is a tally of the three actions, never a blended score.
 *
 * Analysis is fetched per group when the group is opened, and cached server-side for an
 * hour, so opening the page costs nothing until you look at something.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails, Card, CardContent,
  Grid, Tooltip, CircularProgress, Button, Select, MenuItem, FormControl, InputLabel,
  Divider, Alert, LinearProgress,
} from '@mui/material';
import {
  ExpandMore, Science, ShowChart, Assessment, Timeline, Refresh, OpenInNew,
} from '@mui/icons-material';
import apiService from '../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface LabStock {
  symbol: string; name?: string; description?: string; stage?: string | null;
  source?: string; theme?: string; expires_at?: string | null;
  source_article_title?: string; source_article_url?: string;
}
interface LabGroup {
  id: string; name: string; emoji?: string; color?: string; description?: string;
  why_it_matters?: string; source?: string; kind: 'category' | 'radar'; stocks: LabStock[];
}
interface FeatureLine {
  action?: string | null; target_price?: number | null; stop_loss?: number | null;
  expected_return_pct?: number | null; [k: string]: any;
}
interface Analysis {
  symbol: string; is_private?: boolean; current_price?: number | null; duration?: string;
  volatility_range?: FeatureLine | null; stock_scoring?: FeatureLine | null;
  trading_signals?: FeatureLine | null;
  verdict?: { action: string; bullish: number; bearish: number; neutral: number; features: number } | null;
  errors?: Record<string, string>; analyzed_at?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (v?: number | null, d = 2) => (v == null || Number.isNaN(v) ? '—' : `$${v.toFixed(d)}`);
const pct = (v?: number | null) => (v == null || Number.isNaN(v) ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`);

function actionColor(a?: string | null): 'success' | 'error' | 'warning' | 'default' {
  const u = (a || '').toUpperCase();
  if (u.includes('BUY')) return 'success';
  if (u.includes('SELL')) return 'error';
  if (u === 'MIXED') return 'warning';
  return 'default';
}

function ActionChip({ action, size = 'small' }: { action?: string | null; size?: 'small' | 'medium' }) {
  return (
    <Chip label={action || 'n/a'} color={actionColor(action)} size={size}
      sx={{ fontWeight: 'bold', height: size === 'small' ? 20 : 26, fontSize: size === 'small' ? '0.65rem' : '0.8rem' }} />
  );
}

// ── One feature's bottom line ────────────────────────────────────────────────

function FeatureRow({ icon, title, horizon, line, color, error, children }: {
  icon: React.ReactNode; title: string; horizon: string; line?: FeatureLine | null;
  color: string; error?: string; children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ borderLeft: `3px solid ${color}`, pl: 1, py: 0.5, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" fontWeight="bold" sx={{ color, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {icon} {title}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>{horizon}</Typography>
        <Box sx={{ flex: 1 }} />
        {error ? <Chip label="unavailable" size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
          : <ActionChip action={line?.action} />}
      </Box>
      {error && <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>{error}</Typography>}
      {line && !error && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.3 }}>
          <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>Target <b>{fmt(line.target_price)}</b></Typography>
          <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>Stop <b>{fmt(line.stop_loss)}</b></Typography>
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: (line.expected_return_pct ?? 0) >= 0 ? 'success.main' : 'error.main' }}>
            Exp. <b>{pct(line.expected_return_pct)}</b>
          </Typography>
          {children && (
            <Button size="small" onClick={() => setOpen(o => !o)}
              sx={{ p: 0, minWidth: 0, fontSize: '0.62rem', textTransform: 'none', color }}>
              {open ? 'less' : 'details'}
            </Button>
          )}
        </Box>
      )}
      {open && children}
    </Box>
  );
}

// ── Stock card ────────────────────────────────────────────────────────────────

function LabCard({ stock, a, color }: { stock: LabStock; a?: Analysis; color: string }) {
  const isPrivate = stock.symbol.startsWith('PRIVATE:');
  const vr = a?.volatility_range; const sc = a?.stock_scoring; const ts = a?.trading_signals;
  const ind = ts?.indicators || {};
  return (
    <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
      '&:hover': { borderColor: color } }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight="bold" sx={{ color, lineHeight: 1.1 }}>{stock.symbol.replace('PRIVATE:', '')}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 220 }}>{stock.name}</Typography>
            {stock.theme && <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>{stock.theme}</Typography>}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {a?.current_price != null && <Typography fontWeight="bold" sx={{ lineHeight: 1.1 }}>{fmt(a.current_price)}</Typography>}
            {a?.verdict && (
              <Tooltip title={`${a.verdict.bullish} bullish · ${a.verdict.bearish} bearish · ${a.verdict.neutral} neutral of ${a.verdict.features} features — a tally, not a score`}>
                <span><ActionChip action={a.verdict.action} /></span>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />

        {isPrivate ? (
          <Typography variant="caption" color="text.disabled">Private company — no market data to cross-check.</Typography>
        ) : !a ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={12} /><Typography variant="caption" color="text.disabled">analysing…</Typography>
          </Box>
        ) : (
          <>
            <FeatureRow icon={<ShowChart sx={{ fontSize: 13 }} />} title="Volatility Range" horizon={vr?.validity || '1–2 wk'}
              line={vr} color="#3B82F6" error={a.errors?.volatility_range}>
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>{vr?.reason}</Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>
                  Range {fmt(vr?.range_lower)} – {fmt(vr?.range_upper)} · zone {vr?.zone} · confidence {vr?.confidence != null ? Math.round(vr.confidence * 100) + '%' : '—'}
                </Typography>
              </Box>
            </FeatureRow>

            <FeatureRow icon={<Assessment sx={{ fontSize: 13 }} />} title={`Stock Scoring ${sc?.overall_score != null ? sc.overall_score.toFixed(0) + '/100' : ''}`}
              horizon={sc?.timeframe || '3–6 mo'} line={sc} color="#8B5CF6" error={a.errors?.stock_scoring}>
              <Box sx={{ mt: 0.5 }}>
                {sc?.breakdown && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.3, mb: 0.5 }}>
                    {Object.entries(sc.breakdown).map(([k, v]: any) => (
                      <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', width: 68, textTransform: 'capitalize' }}>{k}</Typography>
                        <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, v))} sx={{ flex: 1, height: 4, borderRadius: 2 }} />
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', width: 24, textAlign: 'right' }}>{Math.round(v)}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {(sc?.reasons || []).slice(0, 4).map((r: string, i: number) => (
                  <Typography key={i} variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', display: 'block' }}>• {r}</Typography>
                ))}
                {sc?.consensus && (
                  <Box sx={{ mt: 0.5, p: 0.5, bgcolor: '#8B5CF611', borderRadius: 1 }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.62rem', display: 'block' }}>
                      Cross-Feature Consensus: {sc.consensus.label}
                    </Typography>
                    {(sc.consensus.votes || []).map((v: any, i: number) => (
                      <Typography key={i} variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>
                        {v.feature}: <b>{v.signal}</b> ({Math.round((v.confidence || 0) * 100)}%) — {v.short_reason}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </FeatureRow>

            <FeatureRow icon={<Timeline sx={{ fontSize: 13 }} />} title={`Trading Signals${ts?.confidence != null ? ' · ' + ts.confidence + '%' : ''}`}
              horizon={ts?.timeframe || '1–2 wk'} line={ts} color="#F59E0B" error={a.errors?.trading_signals}>
              <Box sx={{ mt: 0.5 }}>
                {(ts?.analysis || []).map((r: string, i: number) => (
                  <Typography key={i} variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', display: 'block' }}>{r}</Typography>
                ))}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 8px', mt: 0.5 }}>
                  {[
                    ['RSI', ind.rsi != null ? `${ind.rsi.toFixed(1)} ${ind.rsi_status || ''}` : '—'],
                    ['MACD', ind.macd_status || '—'],
                    ['Trend', ind.trend || '—'],
                    ['Bollinger', ind.bb_position ? `${ind.bb_position}${ind.bb_squeeze ? ' · squeeze' : ''}` : '—'],
                    ['Stoch', ind.stoch_k != null ? `${ind.stoch_k.toFixed(0)} / ${(ind.stoch_d ?? 0).toFixed(0)}` : '—'],
                    ['Volume', ind.volume_status || '—'],
                    ['vs SMA20/50/200', `${ind.price_above_sma20 ? '↑' : '↓'} ${ind.price_above_sma50 ? '↑' : '↓'} ${ind.price_above_sma200 ? '↑' : '↓'}`],
                    ['Recent signals', `${ts?.recent_buy_signals ?? 0} buy · ${ts?.recent_sell_signals ?? 0} sell`],
                  ].map(([k, v]) => (
                    <Typography key={k as string} variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      {k}: <b>{v}</b>
                    </Typography>
                  ))}
                </Box>
              </Box>
            </FeatureRow>
          </>
        )}

        {stock.source_article_url && (
          <Typography component="a" href={stock.source_article_url} target="_blank" rel="noopener noreferrer" variant="caption"
            sx={{ fontSize: '0.62rem', color: 'text.disabled', display: 'inline-flex', alignItems: 'center', gap: 0.3, textDecoration: 'none', mt: 0.5 }}>
            {(stock.source_article_title || 'source').slice(0, 60)}… <OpenInNew sx={{ fontSize: 10 }} />
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function FutureStocksLab() {
  const [groups, setGroups] = useState<LabGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState('1 Y');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [analysis, setAnalysis] = useState<Record<string, Record<string, Analysis>>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  useEffect(() => {
    (async () => {
      try { const d = await apiService.getFutureStocksLabGroups(); setGroups(d.groups || []); }
      catch (e: any) { setError(e?.response?.data?.detail || e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  const loadGroup = useCallback(async (gid: string, force = false) => {
    setBusy(b => ({ ...b, [gid]: true }));
    try {
      const d = await apiService.getFutureStocksLabGroupAnalysis(gid, duration, force);
      setAnalysis(a => ({ ...a, [gid]: d.analysis || {} }));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(b => ({ ...b, [gid]: false }));
    }
  }, [duration]);

  // A change of lookback invalidates what's shown; reload the open groups.
  useEffect(() => {
    setAnalysis({});
    Object.keys(open).filter(k => open[k]).forEach(k => loadGroup(k));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const toggle = (gid: string) => (_: any, isOpen: boolean) => {
    setOpen(o => ({ ...o, [gid]: isOpen }));
    if (isOpen && !analysis[gid] && !busy[gid]) loadGroup(gid);
  };

  const tally = (gid: string) => {
    const a = analysis[gid]; if (!a) return null;
    const c = { BUY: 0, SELL: 0, HOLD: 0, MIXED: 0 };
    Object.values(a).forEach(x => { const v = x.verdict?.action as keyof typeof c; if (v && v in c) c[v]++; });
    return c;
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Science sx={{ color: '#10B981', fontSize: 30 }} />
        <Typography variant="h5" fontWeight="bold">Future Stocks Lab</Typography>
        <Chip label="EXPERIMENTAL" size="small" color="success" variant="outlined" />
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Lookback</InputLabel>
          <Select value={duration} label="Lookback" onChange={e => setDuration(e.target.value)}>
            <MenuItem value="6 M">6 Months</MenuItem>
            <MenuItem value="1 Y">1 Year</MenuItem>
            <MenuItem value="2 Y">2 Years</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 980, lineHeight: 1.6 }}>
        Every stock from <strong>Stocks of the Future</strong> — and the Top Picks <strong>Future Technology</strong> radar —
        cross-checked at the same lookback by <span style={{ color: '#3B82F6' }}>Volatility Ranges</span>,{' '}
        <span style={{ color: '#8B5CF6' }}>Stock Scoring</span> (with Cross-Feature Consensus) and{' '}
        <span style={{ color: '#F59E0B' }}>Trading Signals</span> (with the indicator breakdown). The three answer different
        questions on different horizons — a decade-long thesis can be a two-week SELL — so the verdict chip is a tally of
        the three, never a blended score. Open a group to analyse it (cached one hour).
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {groups.map(g => {
        const isOpen = !!open[g.id];
        const color = g.color || '#10B981';
        const t = tally(g.id);
        const listed = g.stocks.filter(s => !s.symbol.startsWith('PRIVATE:')).length;
        return (
          <Accordion key={g.id} expanded={isOpen} onChange={toggle(g.id)}
            sx={{ mb: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: isOpen ? color : 'divider',
              borderRadius: '8px !important', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMore sx={{ color }} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '1.3rem', lineHeight: 1 }}>{g.emoji}</Typography>
                <Typography fontWeight="bold" sx={{ color: isOpen ? color : 'text.primary' }}>{g.name}</Typography>
                <Chip label={`${g.stocks.length} stocks`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: `${color}22`, color }} />
                {g.kind === 'radar' && <Chip label="21-day rolling radar" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />}
                {g.source === 'discovered' && <Chip label="auto-discovered" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />}
                <Box sx={{ flex: 1 }} />
                {busy[g.id] && <CircularProgress size={14} />}
                {t && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {t.BUY > 0 && <Chip label={`${t.BUY} BUY`} size="small" color="success" sx={{ height: 18, fontSize: '0.62rem' }} />}
                    {t.HOLD > 0 && <Chip label={`${t.HOLD} HOLD`} size="small" sx={{ height: 18, fontSize: '0.62rem' }} />}
                    {t.MIXED > 0 && <Chip label={`${t.MIXED} MIXED`} size="small" color="warning" sx={{ height: 18, fontSize: '0.62rem' }} />}
                    {t.SELL > 0 && <Chip label={`${t.SELL} SELL`} size="small" color="error" sx={{ height: 18, fontSize: '0.62rem' }} />}
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {g.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.8rem', lineHeight: 1.5 }}>{g.description}</Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button size="small" startIcon={<Refresh />} disabled={busy[g.id] || listed === 0}
                  onClick={() => loadGroup(g.id, true)} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
                  Re-analyse (ignore cache)
                </Button>
              </Box>
              {g.stocks.length === 0 ? (
                <Typography variant="body2" color="text.disabled">No constituents yet.</Typography>
              ) : (
                <Grid container spacing={1.5}>
                  {g.stocks.map(s => (
                    <Grid item xs={12} sm={6} lg={4} xl={3} key={s.symbol}>
                      <LabCard stock={s} a={analysis[g.id]?.[s.symbol]} color={color} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
